import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { Download } from "lucide-react";

function csvUrl(path) {
  const token = localStorage.getItem("jrc_token");
  return `${process.env.REACT_APP_BACKEND_URL}/api/admin/exports/${path}`;
}

const ACTION_BADGE = {
  "application.approved": "bg-emerald-100 text-emerald-900 border-emerald-300",
  "application.rejected": "bg-rose-100 text-rose-900 border-rose-300",
  "application.under_review": "bg-blue-100 text-blue-900 border-blue-300",
  "application.disbursed": "bg-violet-100 text-violet-900 border-violet-300",
  "application.submitted": "bg-amber-100 text-amber-900 border-amber-300",
  "loan.installment_paid": "bg-emerald-100 text-emerald-900 border-emerald-300",
  "crm.note_added": "bg-violet-100 text-violet-900 border-violet-300",
};

export default function AdminAudit() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/audit?limit=500")
      .then(({ data }) => setItems(data || []))
      .finally(() => setLoading(false));
  }, []);

  const handleExport = async (filename) => {
    // Use fetch + Bearer to download CSV (Authorization can't be added to <a href>)
    const token = localStorage.getItem("jrc_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/exports/${filename}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout role="admin">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-ink sm:text-3xl">Audit log</h1>
          <p className="mt-1 text-sm text-ink-soft">Every admin write action — approvals, rejections, repayments, CRM notes, exports.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            ["jrc_applications.csv", "applications.csv"],
            ["jrc_loans.csv", "loans.csv"],
            ["jrc_customers.csv", "customers.csv"],
            ["jrc_contact_messages.csv", "contact-messages.csv"],
          ].map(([fname, ep]) => (
            <button
              key={ep}
              onClick={() => handleExport(ep)}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#5B2C82] px-3 py-1.5 text-xs font-semibold text-[#5B2C82] hover:bg-[#5B2C82] hover:text-white"
              data-testid={`audit-export-${ep.replace(/\.csv$/, "")}`}
            >
              <Download className="h-3.5 w-3.5" /> {ep.replace(".csv", "")}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-[#E5E2DE] bg-white">
        <div className="hidden grid-cols-12 gap-4 border-b border-[#E5E2DE] bg-[#F9F6F0] px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-muted md:grid">
          <div className="col-span-3">When</div>
          <div className="col-span-3">Actor</div>
          <div className="col-span-3">Action</div>
          <div className="col-span-3">Target / Details</div>
        </div>
        {loading && <div className="px-5 py-10 text-center text-sm text-ink-soft">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="px-5 py-10 text-center text-sm text-ink-soft">No audit entries yet.</div>
        )}
        {items.map((a) => (
          <div key={a.id} data-testid={`audit-row-${a.id}`}
            className="grid grid-cols-1 gap-2 border-b border-[#E5E2DE] px-5 py-4 last:border-b-0 md:grid-cols-12 md:gap-4">
            <div className="text-xs text-ink-soft md:col-span-3">{new Date(a.at).toLocaleString()}</div>
            <div className="text-sm md:col-span-3">
              <div className="font-medium text-ink">{a.actor_email}</div>
              <div className="text-xs text-ink-muted">{a.actor_role}</div>
            </div>
            <div className="md:col-span-3">
              <span className={`rounded-full border px-2 py-0.5 text-xs ${ACTION_BADGE[a.action] || "bg-stone-100 text-stone-900 border-stone-300"}`}>
                {a.action}
              </span>
            </div>
            <div className="text-xs text-ink-soft md:col-span-3">
              <div className="font-medium text-ink">{a.target_type} · {a.target_id?.slice?.(0, 12) || a.target_id}</div>
              {a.details && Object.keys(a.details).length > 0 && (
                <div className="mt-1 max-w-md truncate text-ink-muted">{JSON.stringify(a.details)}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
